const SMS_JPEG_MAX_EDGE = 1280;
const SMS_JPEG_QUALITY = 0.72;
const SMS_JPEG_MAX_BYTES = 450 * 1024;

function smsJpegScale(width, height, maxEdge = SMS_JPEG_MAX_EDGE) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w < 1 || h < 1) return { width: 0, height: 0 };
  const edge = Math.max(w, h);
  if (edge <= maxEdge) {
    return { width: Math.round(w), height: Math.round(h) };
  }
  const scale = maxEdge / edge;
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

async function encodeSmsJpeg(image, deps = {}) {
  const encode = deps.encode;
  const maxBytes = deps.maxBytes || SMS_JPEG_MAX_BYTES;
  let edge = deps.maxEdge || SMS_JPEG_MAX_EDGE;
  let quality = deps.quality || SMS_JPEG_QUALITY;
  let last = null;
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const size = smsJpegScale(image.width, image.height, edge);
    const blob = await encode(image, size, quality);
    last = blob;
    if (!blob || blob.size <= maxBytes) return blob;
    if (quality > 0.5) quality = Math.round((quality - 0.12) * 100) / 100;
    else edge = Math.max(480, Math.round(edge * 0.75));
  }
  return last;
}

async function decodeSmsPhotoFile(file) {
  if (!file) throw new Error("photo");
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      source: bitmap,
      close: () => bitmap.close && bitmap.close(),
    };
  }
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("photo"));
      el.src = url;
    });
    return { width: image.naturalWidth, height: image.naturalHeight, source: image };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function encodeCanvasJpeg(image, size, quality) {
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size.width, size.height);
  ctx.drawImage(image.source || image, 0, 0, size.width, size.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("photo"))),
      "image/jpeg",
      quality,
    );
  });
}

async function smsPhotoToJpeg(file, deps = {}) {
  const decode = deps.decode || decodeSmsPhotoFile;
  const image = await decode(file);
  try {
    if (!image || image.width < 1 || image.height < 1) throw new Error("photo");
    const encode = deps.encode || encodeCanvasJpeg;
    const blob = await encodeSmsJpeg(image, { ...deps, encode });
    if (!blob || !blob.size) throw new Error("photo");
    return blob;
  } finally {
    if (image && typeof image.close === "function") image.close();
  }
}

function blobToJpegDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("photo"));
    reader.readAsDataURL(blob);
  });
}

module.exports = {
  SMS_JPEG_MAX_EDGE,
  SMS_JPEG_QUALITY,
  SMS_JPEG_MAX_BYTES,
  smsJpegScale,
  encodeSmsJpeg,
  smsPhotoToJpeg,
  blobToJpegDataUrl,
};
